/* eslint-disable max-classes-per-file */
import { Injectable, OnDestroy } from '@angular/core';
import { EMPTY, fromEvent, Observable } from 'rxjs';
import { share, tap } from 'rxjs/operators';
import { ApBaseClass } from 'webcommon/shared/ap-base-class-ng1520';
import { PrefixedLogger } from 'webcommon/shared/helpers-ng1520';
import { throwSyncError } from 'webcommon/shared/errors-ng1520';

export interface CrossWindowEventTypes {
  // idk what data we would actually be sending right now?
  'aprima-logoff-sync': null;
  'aprima-login-sync': null;
}

// fire/listen events between different tabs/windows of the same app
@Injectable({
  providedIn: 'root',
})
export class CrossWindow extends ApBaseClass {
  private readonly _activeChannelByKey =
    new Map<keyof CrossWindowEventTypes, ApBroadcastChannel$<CrossWindowEventTypes[keyof CrossWindowEventTypes]>>();

  // If we need this more granular, then i guess, we will have to put this check before every call to fireEvent() or on()
  // But the reality is that normally, you wouldn't want to be able to fire the event, and not listen for it, or vice versa.
  private _enabledByKey?: {
    'aprima-logoff-sync': boolean;
    'aprima-login-sync': boolean;
  };
  protected get enabledByKey() {
    return this._enabledByKey ?? throwSyncError(new Error(`CrossWindow: not setup`));
  }

  constructor() {
    super();
    this.onDestroy$.subscribe(() => {
      this._activeChannelByKey.forEach((val, key, map) => {
        val.ngOnDestroy();
        map.delete(key);
      });
    });
  }

  // todo: make some event flags optional? idk if this is necessary
  globalSetup(config: typeof this.enabledByKey): void {
    this._enabledByKey = config;
  }

  on<TName extends keyof CrossWindowEventTypes>(
    eventName: TName,
  ): Observable<MessageEvent<CrossWindowEventTypes[TName]>> {
    return new Observable<MessageEvent<CrossWindowEventTypes[TName]>>((subscriber) => {
      let channel: ApBroadcastChannel$<CrossWindowEventTypes[TName]> | undefined = this._activeChannelByKey.get(eventName);
      if (!channel) {
        channel = createBroadcastChannel$(eventName, this.enabledByKey[eventName]);
        this._activeChannelByKey.set(eventName, channel);
      }

      const sub = channel.messages$.subscribe(subscriber);

      return () => {
        sub.unsubscribe();
      };
    });
  }

  fireEvent<TName extends keyof CrossWindowEventTypes>(
    eventName: TName,
    data: CrossWindowEventTypes[TName],
  ): void {
    let channel: ApBroadcastChannel$<CrossWindowEventTypes[TName]> | undefined = this._activeChannelByKey.get(eventName);
    if (!channel) {
      channel = createBroadcastChannel$(eventName, this.enabledByKey[eventName]);
      this._activeChannelByKey.set(eventName, channel);
    }
    channel.postMessage(data);
  }
}

interface ApBroadcastChannel$<TData> extends OnDestroy {
  readonly name: string;
  readonly messages$: Observable<MessageEvent<TData>>;
  postMessage(data: TData): void;
}


// rxjs wrapper of BroadcastChannel
class ApBroadcastChannel$_Impl<TData> extends ApBaseClass implements ApBroadcastChannel$<TData> {
  private readonly prefixedLogger = new PrefixedLogger(`BroadcastChannel|${this.name} --`, 'darkturquoise');

  // https://web.dev/articles/sign-out-best-practices#consistent_user_experience_across_tabs
  // if we want to actually have an event handled on this exact object reference,
  // then i think we can use BroadcastChannel.dispatchEvent() ??
  private readonly bc = new BroadcastChannel(this.name);
  readonly messages$: Observable<MessageEvent<TData>> = fromEvent<MessageEvent<TData>>(this.bc, 'message').pipe(
    tap((event) => {
      this.prefixedLogger.logDevMode(() => [`Received event`, event]);
    }),
    share(),
  );

  constructor(
    readonly name: string,
  ) {
    super();
    this.prefixedLogger.logDevMode(() => ['initialized']);
    this.onDestroy$.subscribe(() => {
      this.bc.close();
    });
  }

  postMessage(data: TData): void {
    this.bc.postMessage(data);
  }
}

// noop version of BroadcastChannel$, when we know we want all of these events disabled for this configuration/environment
class ApBroadcastChannel$_Noop<TData> extends ApBaseClass implements ApBroadcastChannel$<TData> {
  private readonly prefixedLogger = new PrefixedLogger(`BroadcastChannel|${this.name} --`, 'darkturquoise');

  readonly messages$: Observable<MessageEvent<TData>> = EMPTY;

  constructor(
    readonly name: string,
  ) {
    super();
    this.prefixedLogger.logDevMode(() => ['NOOP']);
  }

  postMessage(_data: TData): void {
    // noop
  }
}

// create a BroadcastChannel$ based on an event map
function createBroadcastChannel$<TName extends keyof CrossWindowEventTypes>(
  name: TName,
  enabled: boolean,
): ApBroadcastChannel$<CrossWindowEventTypes[TName]> {
  if (!enabled) {
    return new ApBroadcastChannel$_Noop<CrossWindowEventTypes[TName]>(name);
  }
  return new ApBroadcastChannel$_Impl<CrossWindowEventTypes[TName]>(name);
}



// mainly for tests atm
export async function createScopedChannel(
  name: string,
  actionFn: (channel: BroadcastChannel) => (void | Promise<void>),
): Promise<void> {
  const channel = new BroadcastChannel(name);
  try {
    await actionFn(channel);
  } finally {
    channel.close();
  }
}
